import { AuditLogTable, MonthPager } from "./_components";
import { useParams } from "@/router";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useQuery, type ApolloError } from "@apollo/client";
import { AUDIT_LOG_PAGINATION_QUERY } from "./queries";
import { useEffect, useState } from "react";
import { useParams as useUrlParams, useSearchWithParams } from "@/hooks/useSearchParams";
import usePagination from "@/hooks/usePagination";
import { DEFAULT_PAGE_SIZE } from "@/components/Pagination/Pagination";
import { DateTime } from "luxon";
import SearchInput from "@/components/SearchInput";
import EmptyState from "@/components/EmptyState";
import { TableControls, TableFooter, TableHeading } from "@/components/Table";
import { SectionDetailsHeader } from "../_components/SectionDetailsHeader";
import { SectionPeopleTabs } from "../_components/SectionPeopleTabs";
import usePageTitle from "@/hooks/usePageTitle";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { sendToRaygun } from "@/lib/raygunUtils";
import {
  AUDIT_CATEGORY_LABELS,
  DEFAULT_AUDIT_CATEGORIES,
  SECTION_AUDIT_CATEGORIES,
  type AuditEventCategory
} from "./categories";

const path = "/sections/:sectionId/audit-log";

function parseMonth(value?: string) {
  const parsed = value ? DateTime.fromFormat(value, "yyyy-MM") : DateTime.invalid("empty");
  const month = (parsed.isValid ? parsed : DateTime.now()).startOf("month");
  const currentMonth = DateTime.now().startOf("month");
  return month > currentMonth ? currentMonth : month;
}

function parseCategories(value?: string): AuditEventCategory[] {
  if (!value) return [...DEFAULT_AUDIT_CATEGORIES];

  const selected = value
    .split(",")
    .filter((category): category is AuditEventCategory =>
      SECTION_AUDIT_CATEGORIES.includes(category as AuditEventCategory)
    );

  return selected.length ? selected : [...DEFAULT_AUDIT_CATEGORIES];
}

function sameCategories(left: AuditEventCategory[], right: AuditEventCategory[]) {
  if (left.length !== right.length) return false;
  return left.every((category) => right.includes(category));
}

export default function AuditLog() {
  useRequireCapability({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewPeople });
  const { sectionId } = useParams(path);
  const { params, addSearchParam, deleteSearchParams } = useUrlParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const { createHeaderPagination, createFooterPagination, paginationVariables, resetPagination } = usePagination();
  const { setError, clearError } = useErrorBanner();

  const month = parseMonth(params.month);
  const categories = parseCategories(params.categories);
  const searchQuery = debouncedSearch.trim() || null;
  const isSearching = !!searchQuery;
  const monthParam = month.toFormat("yyyy-MM");
  const monthKey = month.toISODate();
  const categoriesKey = categories.join(",");
  const filterKey = `${searchQuery ?? ""}|${monthKey}|${categoriesKey}`;
  const [pagedFilterKey, setPagedFilterKey] = useState(filterKey);
  const filterChanged = pagedFilterKey !== filterKey;

  // Keep URL-backed month in sync when a deep link is clamped (e.g. future ?month=).
  useEffect(() => {
    if (params.month && params.month !== monthParam) {
      addSearchParam("month", monthParam);
    }
  }, [params.month, monthParam, addSearchParam]);

  useEffect(() => {
    resetPagination();
    setPagedFilterKey(filterKey);
  }, [searchQuery, monthKey, categoriesKey, filterKey, resetPagination]);

  const queryPagination = filterChanged
    ? { first: paginationVariables.first || paginationVariables.last || DEFAULT_PAGE_SIZE }
    : paginationVariables;

  const queryVariables = {
    sectionId,
    searchQuery,
    categories,
    ...queryPagination,
    ...(isSearching
      ? {}
      : {
          startDateTime: month.startOf("month").toUTC().toISO(),
          endDateTime: month.endOf("month").toUTC().toISO()
        })
  };

  const { data, previousData, loading, error, refetch } = useQuery(AUDIT_LOG_PAGINATION_QUERY, {
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => clearError(),
    onError: (apolloError: ApolloError) => {
      sendToRaygun(apolloError, { tags: ["section-audit-log"] });
      setError(apolloError.message);
    }
  });

  // Keep section name for the document title while the first page loads; do not
  // feed previousData into the table so month/filter refetches cannot stamp
  // stale rows under already-updated pager/chips (mirror roster-activity).
  const sectionName = (data ?? previousData)?.section?.name ?? "Section";
  usePageTitle(path, `Sections | Section Details | Audit Log | ${sectionName}`);
  const auditLog = data?.section.auditLogPagination;
  // Complete single-page responses: prefer visible row count over API totalCount.
  const hasNextPage = auditLog?.pageInfo?.hasNextPage === true;
  const hasPreviousPage = auditLog?.pageInfo?.hasPreviousPage === true;
  const presentationTotalCount = !auditLog
    ? undefined
    : !hasNextPage && !hasPreviousPage
      ? (auditLog.nodes?.length ?? 0)
      : auditLog.totalCount;
  const hasActiveFilters = isSearching || categories.length > 0;
  const showLoadError = !!error && !auditLog;

  function setMonth(nextMonth: DateTime) {
    addSearchParam("month", nextMonth.toFormat("yyyy-MM"));
  }

  function toggleCategory(category: AuditEventCategory) {
    const next = categories.includes(category)
      ? categories.filter((value) => value !== category)
      : [...categories, category];

    if (!next.length) return;

    if (sameCategories(next, DEFAULT_AUDIT_CATEGORIES)) {
      deleteSearchParams("categories");
      return;
    }

    addSearchParam("categories", next.join(","));
  }

  const paginationProps = { loading, totalCount: presentationTotalCount };

  function renderControls(_table?: unknown, { onSortClick }: { onSortClick?: () => void } = {}) {
    return (
      <TableControls
        pagination={createHeaderPagination(auditLog, paginationProps)}
        onSortClick={onSortClick}
        useExpandedFilters={false}
        filterChipsAriaLabel="Change Type"
        filterChips={SECTION_AUDIT_CATEGORIES.map((category) => ({
          label: AUDIT_CATEGORY_LABELS[category],
          selected: categories.includes(category),
          softDisabled: categories.length === 1 && categories.includes(category),
          onClick: () => toggleCategory(category)
        }))}
        afterSearch={!isSearching ? <MonthPager month={month} onChange={setMonth} /> : undefined}
      >
        <SearchInput
          placeholder="Search Log"
          query={searchValue}
          onChange={(value) => {
            setSearchValue(value);
            resetPagination();
          }}
          onClear={() => {
            clearSearch();
            resetPagination();
          }}
        />
      </TableControls>
    );
  }

  return (
    <div data-testid="section-audit-log-page">
      <SectionDetailsHeader sectionId={sectionId} />
      <SectionPeopleTabs />
      <TableHeading
        heading="Audit Log"
        body={
          showLoadError ? undefined : "A history of all data changes associated with this section can be found below."
        }
      />
      {showLoadError ? (
        <>
          {renderControls()}
          <EmptyState
            iconName="error"
            title="Unable to Load Audit Log"
            caption="Try refreshing the page. If the problem continues, contact support."
            action={{ children: "Try Again", onClick: () => refetch() }}
          />
        </>
      ) : (
        <AuditLogTable
          data={auditLog?.nodes || []}
          loading={loading}
          hasActiveFilters={hasActiveFilters}
          renderControls={renderControls}
          renderFooter={() => <TableFooter pagination={createFooterPagination(auditLog, paginationProps)} />}
        />
      )}
    </div>
  );
}
